/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// DepletionsGrid.jsx — ported from the source app (logic verbatim; only CSS
// imports are module-local). Pivots case-sales into a (dimensions × months)
// grid with resizable/sticky dimension columns, sortable headers, and totals.
import React, { useMemo, useState, useCallback, useRef } from "react";
import "./di-grid.css";
import "./DepletionsGrid.css";

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function colKey(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

const MONTH_COL_W = 74;
const TOTAL_COL_W = 80;
const MIN_COL_W   = 40;

// The ungrouped view WITH retail-account detail. Kept separate from the plain
// one below because most tenants' data is state-level: adding Chain/Store
// unconditionally would give them two columns of "—" on every row. The grid
// picks between them from the data (see `cfg` in the component).
const DETAIL_CONFIG = {
  dims: [
    { id: "chain",       label: "Chain",        defaultW: 130 },
    { id: "account",     label: "Store",        defaultW: 180 },
    // Address is ingested for outlet identity and geocoding; without a column it
    // was captured and never shown anywhere. Columns are resizable, so a long
    // street address costs nothing to anyone who doesn't want it.
    { id: "address",     label: "Address",      defaultW: 190 },
    { id: "city",        label: "City",         defaultW: 110 },
    { id: "state",       label: "State",        defaultW: 90  },
    { id: "distributor", label: "Distributor",  defaultW: 140 },
    { id: "flavor",      label: "Flavor",       defaultW: 120 },
    { id: "size",        label: "Size",         defaultW: 80  },
    { id: "pkg",         label: "Package",      defaultW: 110 },
  ],
  getKey: r => [
    r.chain_name ?? "", r.account_name ?? "", r.account_address ?? "", r.account_city ?? "",
    r.states?.state_name ?? "", r.states?.distributor ?? "",
    r.flavors?.name ?? "", r.sizes?.name ?? "", r.pack_sizes?.name ?? "",
  ].join("\x00"),
  getVals: r => ({
    chain: r.chain_name ?? "—", account: r.account_name ?? "—",
    address: r.account_address ?? "—", city: r.account_city ?? "—",
    state: r.states?.state_name ?? "—", distributor: r.states?.distributor ?? "—",
    flavor: r.flavors?.name ?? "—", size: r.sizes?.name ?? "—", pkg: r.pack_sizes?.name ?? "—",
  }),
};

// Configuration for each Group By mode
const GROUP_CONFIGS = {
  "": {
    dims: [
      { id: "state",       label: "State",       defaultW: 110 },
      { id: "distributor", label: "Distributor",  defaultW: 150 },
      { id: "flavor",      label: "Flavor",       defaultW: 120 },
      { id: "size",        label: "Size",         defaultW: 80  },
      { id: "pkg",         label: "Package",      defaultW: 110 },
    ],
    getKey:  r => [r.states?.state_name ?? "", r.states?.distributor ?? "", r.flavors?.name ?? "", r.sizes?.name ?? "", r.pack_sizes?.name ?? ""].join("\x00"),
    getVals: r => ({ state: r.states?.state_name ?? "—", distributor: r.states?.distributor ?? "—", flavor: r.flavors?.name ?? "—", size: r.sizes?.name ?? "—", pkg: r.pack_sizes?.name ?? "—" }),
  },
  "chain": {
    dims: [{ id: "chain", label: "Chain", defaultW: 200 }],
    getKey:  r => r.chain_name ?? "—",
    getVals: r => ({ chain: r.chain_name ?? "—" }),
  },
  "account": {
    dims: [{ id: "account", label: "Store", defaultW: 240 }],
    getKey:  r => r.account_name ?? "—",
    getVals: r => ({ account: r.account_name ?? "—" }),
  },
  // One row per door, so the address is what tells two same-named stores apart.
  "chain_account": {
    dims: [
      { id: "chain",   label: "Chain",   defaultW: 150 },
      { id: "account", label: "Store",   defaultW: 220 },
      { id: "address", label: "Address", defaultW: 200 },
      { id: "city",    label: "City",    defaultW: 120 },
    ],
    getKey:  r => `${r.chain_name ?? "—"}\x00${r.account_name ?? "—"}\x00${r.account_address ?? "—"}\x00${r.account_city ?? "—"}`,
    getVals: r => ({ chain: r.chain_name ?? "—", account: r.account_name ?? "—", address: r.account_address ?? "—", city: r.account_city ?? "—" }),
  },
  "region": {
    dims: [{ id: "region", label: "Region", defaultW: 160 }],
    getKey:  r => r.states?.region      ?? "—",
    getVals: r => ({ region: r.states?.region      ?? "—" }),
  },
  "state": {
    dims: [{ id: "state", label: "State", defaultW: 160 }],
    getKey:  r => r.states?.state_name  ?? "—",
    getVals: r => ({ state: r.states?.state_name  ?? "—" }),
  },
  "distributor": {
    dims: [{ id: "distributor", label: "Distributor", defaultW: 200 }],
    getKey:  r => r.states?.distributor ?? "—",
    getVals: r => ({ distributor: r.states?.distributor ?? "—" }),
  },
  "flavor": {
    dims: [{ id: "flavor", label: "Flavor", defaultW: 160 }],
    getKey:  r => r.flavors?.name       ?? "—",
    getVals: r => ({ flavor: r.flavors?.name       ?? "—" }),
  },
  "size": {
    dims: [{ id: "size", label: "Bottle Size", defaultW: 140 }],
    getKey:  r => r.sizes?.name         ?? "—",
    getVals: r => ({ size: r.sizes?.name         ?? "—" }),
  },
  "pkg": {
    dims: [{ id: "pkg", label: "Package", defaultW: 140 }],
    getKey:  r => r.pack_sizes?.name    ?? "—",
    getVals: r => ({ pkg: r.pack_sizes?.name    ?? "—" }),
  },
  "flavor_size": {
    dims: [
      { id: "flavor", label: "Flavor",       defaultW: 140 },
      { id: "size",   label: "Bottle Size",  defaultW: 120 },
    ],
    getKey:  r => `${r.flavors?.name ?? "—"}\x00${r.sizes?.name ?? "—"}`,
    getVals: r => ({ flavor: r.flavors?.name ?? "—", size: r.sizes?.name ?? "—" }),
  },
};

export default function DepletionsGrid({ rows, filterYear, groupBy = "" }) {
  const [colWidths, setColWidths] = useState({});
  const [sortCol,   setSortCol]   = useState(null);
  const [sortDir,   setSortDir]   = useState("asc");
  const dragRef = useRef(null);

  const handleSort = useCallback((col) => {
    setSortCol(prev => {
      if (prev === col) {
        setSortDir(d => d === "asc" ? "desc" : "asc");
        return prev;
      }
      setSortDir("asc");
      return col;
    });
  }, []);

  // Does this tenant's data actually carry store detail? Cheap scan — the rows
  // are already in memory — and it decides whether the ungrouped view shows the
  // Chain/Store/City columns or the original state-level ones.
  const hasOutletDetail = useMemo(
    () => rows.some(r => r.chain_name || r.account_name),
    [rows]
  );

  const cfg = groupBy
    ? (GROUP_CONFIGS[groupBy] || GROUP_CONFIGS[""])
    : (hasOutletDetail ? DETAIL_CONFIG : GROUP_CONFIGS[""]);
  const activeDims = cfg.dims;

  const getW = useCallback((id, def) => colWidths[id] ?? def, [colWidths]);

  // Compute sticky left offset for each dim column
  const stickyLeft = useMemo(() => {
    let offset = 0;
    return activeDims.map(c => {
      const left = offset;
      offset += getW(c.id, c.defaultW);
      return left;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDims, getW]);

  const startResize = useCallback((colId, defaultW, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[colId] ?? defaultW;
    dragRef.current = { colId, startX, startW };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const { colId, startX, startW } = dragRef.current;
      setColWidths(prev => ({
        ...prev,
        [colId]: Math.max(MIN_COL_W, startW + ev.clientX - startX),
      }));
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [colWidths]);

  const { pivotRows, activeCols, colTotals, grandTotal, unitLabel } = useMemo(() => {
    if (!rows.length) return { pivotRows: [], activeCols: [], colTotals: [], grandTotal: 0, unitLabel: "" };

    let visibleRows, multiYear, unitLabel;
    if (filterYear) {
      visibleRows = rows.filter(r => r.year === Number(filterYear));
      multiYear   = false;
      unitLabel   = `Depletion Sales in Physical Cases — ${filterYear}`;
    } else {
      visibleRows = rows;
      multiYear   = true;
      unitLabel   = `Depletion Sales in Physical Cases — All Years`;
    }

    if (!visibleRows.length) return { pivotRows: [], activeCols: [], colTotals: [], grandTotal: 0, unitLabel };

    const { getKey, getVals } = cfg;

    const groups = {};
    visibleRows.forEach(r => {
      const dimKey = getKey(r);
      if (!groups[dimKey]) {
        groups[dimKey] = { ...getVals(r), months: {} };
      }
      const ck = colKey(r.year, r.month);
      groups[dimKey].months[ck] = (groups[dimKey].months[ck] ?? 0) + (Number(r.case_sales) || 0);
    });

    const pivotRows = Object.values(groups).sort((a, b) => {
      const aKey = activeDims.map(c => a[c.id] ?? "").join("\x00");
      const bKey = activeDims.map(c => b[c.id] ?? "").join("\x00");
      if (aKey < bKey) return -1;
      if (aKey > bKey) return 1;
      return 0;
    });

    const colKeySet = new Set();
    pivotRows.forEach(r => Object.keys(r.months).forEach(k => colKeySet.add(k)));
    const activeCols = [...colKeySet].sort().map(k => {
      const [y, m] = k.split("-");
      const label  = multiYear ? `${MONTH_ABBR[Number(m) - 1]} ${String(y).slice(2)}` : MONTH_ABBR[Number(m) - 1];
      return { key: k, label };
    });

    const colTotals  = activeCols.map(({ key }) => pivotRows.reduce((s, r) => s + (r.months[key] ?? 0), 0));
    const grandTotal = colTotals.reduce((a, b) => a + b, 0);
    return { pivotRows, activeCols, colTotals, grandTotal, unitLabel };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filterYear, cfg, activeDims]);

  const sortedPivotRows = useMemo(() => {
    if (!sortCol) return pivotRows;
    return [...pivotRows].sort((a, b) => {
      let av, bv;
      if (sortCol === "total") {
        av = activeCols.reduce((s, { key }) => s + (a.months[key] ?? 0), 0);
        bv = activeCols.reduce((s, { key }) => s + (b.months[key] ?? 0), 0);
        return sortDir === "asc" ? av - bv : bv - av;
      }
      if (activeCols.find(c => c.key === sortCol)) {
        av = a.months[sortCol] ?? 0;
        bv = b.months[sortCol] ?? 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      av = (a[sortCol] ?? "").toString().toLowerCase();
      bv = (b[sortCol] ?? "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [pivotRows, sortCol, sortDir, activeCols]);

  if (!pivotRows.length) {
    return (
      <div className="grid-prompt">
        <div className="grid-prompt-icon">📊</div>
        <div className="grid-prompt-text">No depletion data for the current filters.</div>
      </div>
    );
  }

  const sortIndicator = (col) => {
    if (sortCol !== col) return <span className="sort-icon sort-icon-idle">↕</span>;
    return <span className="sort-icon">{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  // A <th> with a drag-resize handle on its right edge
  const ResizerTh = ({ colId, defaultW, className, style, onHeaderClick, children }) => {
    const w = getW(colId, defaultW);
    return (
      <th
        className={`${className}${onHeaderClick ? " dep-sortable-th" : ""}`}
        style={{ ...style, width: w, minWidth: w, maxWidth: w }}
        onClick={onHeaderClick}
      >
        {children}
        <div className="dep-resize-handle" onMouseDown={e => { e.stopPropagation(); startResize(colId, defaultW, e); }} />
      </th>
    );
  };

  const lastDimIdx = activeDims.length - 1;

  const dimCells = (row) => activeDims.map((c, i) => {
    const w = getW(c.id, c.defaultW);
    return (
      <td
        key={c.id}
        className={`grid-sku-cell dep-sticky-cell${i === lastDimIdx ? " dep-sticky-last" : ""}`}
        style={{ left: stickyLeft[i], fontWeight: i === 0 ? 600 : 400, width: w, minWidth: w, maxWidth: w, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {row[c.id] ?? "—"}
      </td>
    );
  });

  return (
    <div className="grid-wrap dep-grid-wrap">
      <div className="grid-unit-header">{unitLabel}</div>
      <div className="grid-scroll dep-grid-scroll">
        <table className="forecast-grid dep-table">
          <thead>
            <tr>
              {activeDims.map((c, i) => (
                <ResizerTh
                  key={c.id}
                  colId={c.id}
                  defaultW={c.defaultW}
                  className={`grid-th dep-sticky-th${i === lastDimIdx ? " dep-sticky-last-th" : ""}${i === 0 ? " grid-sku-th" : ""}`}
                  style={{ left: stickyLeft[i], textAlign: "left", paddingLeft: i === 0 ? undefined : 12 }}
                  onHeaderClick={() => handleSort(c.id)}
                >
                  {c.label}{sortIndicator(c.id)}
                </ResizerTh>
              ))}
              {activeCols.map(({ key, label }) => (
                <ResizerTh key={key} colId={key} defaultW={MONTH_COL_W} className="grid-th grid-month-th" onHeaderClick={() => handleSort(key)}>
                  {label}{sortIndicator(key)}
                </ResizerTh>
              ))}
              <ResizerTh colId="total" defaultW={TOTAL_COL_W} className="grid-th grid-total-th" onHeaderClick={() => handleSort("total")}>
                Total{sortIndicator("total")}
              </ResizerTh>
            </tr>
          </thead>

          <tbody>
            {sortedPivotRows.map((r, idx) => {
              const rowTotal = activeCols.reduce((s, { key }) => s + (r.months[key] ?? 0), 0);
              return (
                <tr key={idx} className={idx % 2 === 0 ? "grid-row" : "grid-row grid-row-alt"}>
                  {dimCells(r)}
                  {activeCols.map(({ key }) => {
                    const val = r.months[key] ?? 0;
                    return (
                      <td key={key} className={`grid-data-cell grid-cell-readonly${!val ? " grid-cell-empty" : ""}`}>
                        <span className={`grid-val${!val ? " grid-val-empty" : ""}`}>
                          {val > 0 ? Math.round(val).toLocaleString() : "—"}
                        </span>
                      </td>
                    );
                  })}
                  <td className="grid-row-total">{rowTotal > 0 ? Math.round(rowTotal).toLocaleString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr className="grid-footer-row">
              <td
                className="grid-footer-label dep-sticky-footer"
                style={{ left: stickyLeft[0] }}
                colSpan={activeDims.length}
              >
                Total
              </td>
              {colTotals.map((total, i) => (
                <td key={i} className="grid-footer-cell">{total > 0 ? Math.round(total).toLocaleString() : "—"}</td>
              ))}
              <td className="grid-footer-grand">{grandTotal > 0 ? Math.round(grandTotal).toLocaleString() : "—"}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
